import React, { FC, useState, useRef, useMemo, useEffect, useCallback } from "react";
import { ColumnConfig, numberSort, SortDirections, TextAlignment, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import {
    JobPerformanceProjectRun,
    JobPerformanceDashboardRequest,
} from "@app/operations/job-performance/jobPerformanceInterfaces";
import { JobPerformanceService } from "@app/operations/job-performance/jobPerformanceService";
import { JobPerformanceRunBreakdownPanel } from "@app/operations/job-performance/JobPerformanceRunBreakdownPanel";

type ZipProjectRunRow = {
    [wafflesKeyColumnName]: string;
    client:             string | null;
    dateFinished:       string | null;
    dateFinishedRaw:    string;
    dateStarted:        string | null;
    dateStartedRaw:     string;
    isOutlier:          boolean;
    isQuarantined:      boolean;
    jobs:               number | null;
    project:            string | null;
    projectId:          number | null;
    projectIdFormatted: React.ReactNode;
    projectRunConfigurationId:          number | null;
    projectRunConfigurationIdFormatted: React.ReactNode;
    publishQueueWait:   number | null;
    publishRun:         number | null;
    queue:              string | null;
    queueWait:          number | null;
    runTime:            React.ReactNode | number | null;
    runTimeRaw:         number | null;
    status:             React.ReactNode;
    total:              number | null;
};

const PAGE_SIZE = 10;

const makeKey = (r: JobPerformanceProjectRun) =>
    `${r.clientId}-${r.projectRunConfigurationId}-${r.dateRunStarted}`;

const toRow = (r: JobPerformanceProjectRun): ZipProjectRunRow => {
    const isQuarantined = (r.projectRunTimeMinutes ?? 0) >= 1440; //24 hours
    return {
        client:             r.clientName,
        dateFinished:       new Date(r.dateRunFinished).toLocaleString(),
        dateFinishedRaw:    String(r.dateRunFinished),
        dateStarted:        new Date(r.dateRunStarted).toLocaleString(),
        dateStartedRaw:     String(r.dateRunStarted),
        isOutlier:          r.isOutlier,
        isQuarantined,
        jobs:               r.jobsProcessed,
        project:            r.projectName,
        projectId:          r.projectId,
        projectIdFormatted: r.projectId,
        projectRunConfigurationId:          r.projectRunConfigurationId,
        projectRunConfigurationIdFormatted: r.projectRunConfigurationId,
        publishQueueWait:   null,
        publishRun:         null,
        queue:              r.jobQueueName,
        queueWait:          null,
        runTime:            r.isOutlier
                                ? <span className="jp-outlier-run-time">{r.projectRunTimeMinutes?.toFixed(1)}</span>
                                : r.projectRunTimeMinutes?.toFixed(1) ?? null,
        runTimeRaw:         r.projectRunTimeMinutes,
        status:             isQuarantined
                                ? <span className="jp-chip jp-chip--danger">Quarantined</span>
                                : r.isOutlier
                                    ? <span className="jp-chip jp-chip--active">Outlier</span>
                                    : <span className="jp-chip jp-chip--muted">Normal</span>,
        total:              r.totalEndToEndMinutes,
        [wafflesKeyColumnName]: makeKey(r),
    };
};

const toExpansionRow = (r: JobPerformanceProjectRun): ZipProjectRunRow => ({
    client:             null,
    dateFinished:       null,
    dateFinishedRaw:    '',
    dateStarted:        null,
    dateStartedRaw:     '',
    isOutlier:          false,
    isQuarantined:      false,
    jobs:               null,
    project:            null,
    projectId:          null,
    projectIdFormatted: null,
    projectRunConfigurationId:          null,
    projectRunConfigurationIdFormatted: null,
    publishQueueWait:   r.publishQueueWaitMinutes,
    publishRun:         r.publishRunTimeMinutes,
    queue:              null,
    queueWait:          r.queueWaitMinutes,
    runTime:            r.projectRunTimeMinutes,
    runTimeRaw:         r.projectRunTimeMinutes,
    status:             null,
    total:              r.totalEndToEndMinutes,
    [wafflesKeyColumnName]: `${makeKey(r)}-breakdown`,
});

type Props = {
    filters: JobPerformanceDashboardRequest;
};

export const JobPerformanceZipProjectRunsTable: FC<Props> = ({ filters }) => {
    const service = useRef(new JobPerformanceService()).current;

    const [items, setItems]         = useState<JobPerformanceProjectRun[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading]     = useState(false);
    const [error, setError]         = useState<string | null>(null);

    useEffect(() => {
        setLoading(true);
        setError(null);
        setItems([]);
        setTotalCount(0);
        service.getZipProjectRuns({ ...filters, offset: 0, pageSize: PAGE_SIZE })
            .then(result => {
                setItems(result.items);
                setTotalCount(result.totalCount);
            })
            .catch(() => setError('Failed to load zip project runs. Please try again.'))
            .finally(() => setLoading(false));
    }, [filters]);

    const tableData = useMemo<ZipProjectRunRow[]>(() => items.map(toRow), [items]);

    const dataExpansions = useMemo<Map<string, ZipProjectRunRow[]>>(
        () => new Map(items.map(r => [makeKey(r), [toExpansionRow(r)]])),
        [items]
    );

    const loadMore = useCallback((justChecking: boolean): boolean => {
        if (justChecking) return items.length < totalCount;
        service.getZipProjectRuns({ ...filters, offset: items.length, pageSize: PAGE_SIZE })
            .then(result => {
                setItems(prev => [...prev, ...result.items]);
                setTotalCount(result.totalCount);
            })
            .catch(() => setError('Failed to load more zip project runs. Please try again.'));
        return items.length + PAGE_SIZE < totalCount;
    }, [items.length, totalCount, filters]);

    const renderRunPanel = useCallback((key: string): React.ReactNode => {
        const run = items.find(r => makeKey(r) === key);
        if (!run) return null;
        return <JobPerformanceRunBreakdownPanel run={run} variant="zip" />;
    }, [items]);

    const handleZipProjectRunsSort = useCallback((segment: ZipProjectRunRow[] | undefined, column: keyof ZipProjectRunRow, direction: SortDirections): ZipProjectRunRow[] => {
        const data = segment !== undefined ? segment : [...tableData];
        const mult = direction === SortDirections.Asc ? 1 : -1;
        if (column === 'runTime') {
            return [...data].sort((a, b) => ((a.runTimeRaw ?? 0) - (b.runTimeRaw ?? 0)) * mult);
        }
        if (column === 'dateStarted') {
            return [...data].sort((a, b) => (String(a.dateStartedRaw) > String(b.dateStartedRaw) ? 1 : -1) * mult);
        }
        if (column === 'dateFinished') {
            return [...data].sort((a, b) => (String(a.dateFinishedRaw) > String(b.dateFinishedRaw) ? 1 : -1) * mult);
        }
        if (column === 'status') {
            const rank = (r: ZipProjectRunRow) => r.isQuarantined ? 2 : r.isOutlier ? 1 : 0;
            return [...data].sort((a, b) => (rank(a) - rank(b)) * mult);
        }
        if (column === 'projectIdFormatted' || column === 'projectId') {
            return [...data].sort((a, b) => ((a.projectId ?? 0) - (b.projectId ?? 0)) * mult);
        }
        if (column === 'projectRunConfigurationIdFormatted' || column === 'projectRunConfigurationId') {
            return [...data].sort((a, b) => ((a.projectRunConfigurationId ?? 0) - (b.projectRunConfigurationId ?? 0)) * mult);
        }
        if (column === 'jobs' || column === 'queueWait' || column === 'publishQueueWait' || column === 'publishRun' || column === 'total') {
            return numberSort(data, column, mult);
        }
        return [...data].sort((a, b) => (String(a[column]) > String(b[column]) ? 1 : -1) * mult);
    }, [tableData]);

    const rowDecorator = useCallback((row: ZipProjectRunRow): string =>
        row.isQuarantined ? "danger" : row.isOutlier ? "warning" : "", []);

    const columnConfig: ColumnConfig = useMemo(() => ({
        client:             { displayText: 'Client',                     sortOrder: 10,  testId: 'zip-runs-client' },
        dateFinished:       { displayText: 'Run Finished',               sortOrder: 50,  testId: 'zip-runs-date-finished' },
        dateFinishedRaw:    { displayText: 'Run Finished (raw)', hidden: true,            testId: 'zip-runs-date-finished-raw' },
        dateStarted:        { displayText: 'Run Started',                sortOrder: 40,  testId: 'zip-runs-date-started' },
        dateStartedRaw:     { displayText: 'Run Started (raw)',  hidden: true,            testId: 'zip-runs-date-started-raw' },
        isOutlier:          { displayText: 'Is Outlier',        hidden: true,           testId: 'zip-runs-is-outlier' },
        isQuarantined:      { displayText: 'Is Quarantined',    hidden: true,           testId: 'zip-runs-is-quarantined' },
        jobs:               { alignment: TextAlignment.Right, displayText: 'Jobs Processed',    sizeContentToFit: true, sortOrder: 60,  testId: 'zip-runs-jobs' },
        project:            { displayText: 'Project Name',               sortOrder: 20,  testId: 'zip-runs-project' },
        projectId:          { displayText: 'Project ID',  hidden: true,  sizeContentToFit: true,                testId: 'zip-runs-project-id' },
        projectIdFormatted: { alignment: TextAlignment.Right, displayText: 'Project ID',        sizeContentToFit: true, sortOrder: 30,  testId: 'zip-runs-project-id-formatted' },
        projectRunConfigurationId:          { displayText: 'Project Run Configuration ID', hidden: true,  sizeContentToFit: true,                testId: 'zip-runs-project-run-configuration-id' },
        projectRunConfigurationIdFormatted: { alignment: TextAlignment.Right, displayText: 'Project Run Configuration ID', sizeContentToFit: true, sortOrder: 31,  testId: 'zip-runs-project-run-configuration-id-formatted' },
        publishQueueWait:   { displayText: 'Publish Queue Wait (Min)', hidden: true, sizeContentToFit: true,                testId: 'zip-runs-publish-queue-wait' },
        publishRun:         { displayText: 'Publish Run (Min)',        hidden: true,  sizeContentToFit: true,                testId: 'zip-runs-publish-run' },
        queue:              { displayText: 'Job Queue Name',             sortOrder: 80,  testId: 'zip-runs-queue' },
        queueWait:          { displayText: 'Queue Wait (Min)',         hidden: true,  sizeContentToFit: true,                testId: 'zip-runs-queue-wait' },
        runTime:            { alignment: TextAlignment.Right, displayText: 'Project Run Time (Min)', sizeContentToFit: true, sortOrder: 70, testId: 'zip-runs-run-time' },
        runTimeRaw:         { displayText: 'Run Time (raw)',    hidden: true,           testId: 'zip-runs-run-time-raw' },
        status:             { displayText: 'Status',                    sizeContentToFit: true, sortOrder: 90,  testId: 'zip-runs-status' },
        total:              { displayText: 'Total End-to-End (Min)',   hidden: true,  sizeContentToFit: true,                testId: 'zip-runs-total' },
    }), []);

    if (error) return <p className="jp-error-message">{error}</p>;

    return (
        <Waffles<ZipProjectRunRow>
            columns={columnConfig}
            customSort={handleZipProjectRunsSort}
            data={tableData}
            dataExpansions={dataExpansions}
            loadMore={loadMore}
            loadMoreLabel="See More"
            renderExpansionPanel={renderRunPanel}
            rowDecorator={rowDecorator}
            loading={loading}
            testId="job-performance__zip-project-runs-table"
        />
    );
};
